// Resolves a team workspace's (TW) team-agent workspaces (TAWs).
//
// D-03 SUPERSEDED (41-04, phase-owner decision — see 41-04-SUMMARY.md for
// the full record). The original design listened on
// where('parentWorkspaceId','==',twId) over the /workspaces collection and
// merged the results into useWorkspaceList's allWorkspaces. 41-01's
// Firestore emulator spike proved that list query is DENIED by security
// rules for both the TW owner and a TW member: a rule that is provable for
// a single-document `get()` (isParentTeamOfThis(), firestore.rules' `allow
// get` block) is not automatically provable for a `list` operation when the
// looked-up id comes from a document field rather than the query's own
// equality clause on a static path.
//
// GAP FOUND DURING LIVE DEV ROLLOUT (T-41-04-STALE-GET, this fix): 41-04's
// original replacement resolved TAW ids from the parent TW document's
// teamAgentIds field — but teamAgentIds holds AGENT USER UIDS, not workspace
// document ids (firestore.rules' isChildTeamMemberOfThis()/
// isChildTeamMember() read it directly as uids, and that meaning must never
// change). get(db, 'workspaces', <a user uid>) always misses against real
// data, because a user uid is never a workspace document id — the roster
// resolved empty for every TW. The fix: a NEW, PARALLEL field on the TW doc,
// teamAgentWorkspaceIds, holds the TAW's own WORKSPACE DOCUMENT ids
// (maintained by functions/workspaceLifecycle.js and functions/
// invitations.js — see their T-41-04-STALE-GET comments for the consistency
// invariant these two arrays must keep). This hook now resolves from THAT
// field, get()ing each TAW document directly by its real workspace id. No
// /workspaces list/query is ever issued here. Each individual get() is
// authorized by isParentTeamOfThis() for BOTH the TW owner and a TW member
// (41-01 proved both passing in the emulator), which is what makes this
// shape correctly serve D-02 (member parity) where a list-query shape
// structurally could not (get()-free list authorization, per 41-01, only
// ever covered the TW owner).
//
// COMPOUNDING BUG, also fixed here (still T-41-04-STALE-GET): a getDoc() for
// a NON-EXISTENT document RESOLVES successfully with snap.exists() === false
// — it does NOT reject. A stale or otherwise-missing teamAgentWorkspaceIds
// entry must never be silently dropped from the roster with zero signal;
// that is the exact T-41-04-SILENT failure mode this hook exists to close.
// A missing TAW document is now counted and surfaced as its own distinct
// anomaly, separate from a permission denial and from a generic error.
//
// GAP FOUND DURING LIVE DEV VERIFICATION (T-41-STALE-ROSTER, this fix):
// dropping the onSnapshot listener (above) closed the boot-listener-census
// regression but opened a new one -- nothing refreshes `taws` after a TAW
// document is mutated in place (commission split, inheritance policy,
// rename, member removal). None of those mutations change
// teamAgentWorkspaceIds, which is the ONLY thing this hook's effect deps key
// off (twId + idsKey), so the effect never re-runs and a save appears to
// silently revert for the editing user even though the write succeeded
// (confirmed on keyflowdev via Firestore server updateTime during live
// verification). Re-opening a per-TAW onSnapshot would undo the census fix
// (tests/store/bootListenerCensus.test.js pins it at 4), so this hook now
// also returns `patchTaw`, an imperative, listener-free way for a caller to
// patch ONE resolved TAW's fields in local state immediately after a
// mutation's write is confirmed to have succeeded. It is the caller's job to
// gate the patch on actual success -- see useWorkspaceHandlers.js's header
// comment on why each TAW-mutating handler now returns a truthy value only
// when the write actually landed.
import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../contexts/ToastContext';

// ─── Pure helpers (exported for direct unit testing) ───────────────────────
// This repo's DOM-less node:test runner cannot observe a hook's internal
// setState/useToast calls once an async effect resolves after the render
// phase has already completed (see tests/store/useWorkspaceList.orMerge.test.js's
// and tests/toastContext.test.js's header comments for the same constraint).
// The result-summarization and message-selection logic is therefore
// extracted as pure functions so it is directly testable without a live
// reconciler.

/**
 * Reduce Promise.allSettled results from N getDoc() calls into the resolved
 * TAW docs plus a per-failure-class count.
 */
export function summarizeTawResults(results) {
    const docs = [];
    let deniedCount = 0;
    let missingCount = 0;
    let otherErrorCount = 0;
    (results || []).forEach((result) => {
        if (result.status === 'fulfilled') {
            const snap = result.value;
            if (snap && snap.exists()) {
                docs.push({ id: snap.id, ...snap.data() });
            } else {
                // getDoc() for a non-existent document RESOLVES successfully
                // (snap.exists() === false) -- it does NOT reject. Silently
                // dropping this is the exact T-41-04-SILENT failure mode
                // this hook exists to close: a stale teamAgentWorkspaceIds
                // entry (a removed/transferred/never-existed TAW) would
                // shrink the roster with zero signal. Count and surface it,
                // distinct from a permission denial or a generic error.
                missingCount += 1;
            }
        } else if (result.reason?.code === 'permission-denied') {
            deniedCount += 1;
        } else {
            otherErrorCount += 1;
        }
    });
    return { docs, deniedCount, missingCount, otherErrorCount };
}

/**
 * Patch ONE TAW's fields inside a taws array without a re-fetch or a
 * listener (T-41-STALE-ROSTER). Pure so it is directly testable without a
 * live reconciler, matching this file's summarizeTawResults/
 * resolveTawLoadErrorMessage convention.
 *
 * @param {Array} taws - the current roster.
 * @param {string} tawId - the id of the TAW to patch.
 * @param {Object|Function} updates - a plain object merged shallowly onto
 *   the matching TAW, or a function `(currentTaw) => partialUpdates` for a
 *   patch that needs to read the current value first (e.g. merging into a
 *   nested object like commissionSplit or inheritancePolicy rather than
 *   replacing it outright).
 * @returns {Array} a NEW array with the matching TAW patched, or the SAME
 *   array reference when tawId/updates are falsy or tawId isn't present --
 *   never re-adds a doc this hook didn't resolve itself, and never touches
 *   any other TAW.
 */
export function applyTawPatch(taws, tawId, updates) {
    if (!tawId || !updates) return taws;
    return (taws || []).map(current => {
        if (current.id !== tawId) return current;
        const patch = typeof updates === 'function' ? updates(current) : updates;
        return patch ? { ...current, ...patch } : current;
    });
}

/**
 * Choose the error toast message for a summarized result, or null when
 * there is nothing to report. Priority: a permission-denied failure (most
 * actionable, a security/sharing problem) outranks a missing document (a
 * data-integrity anomaly), which outranks a generic failure (unclassified).
 */
export function resolveTawLoadErrorMessage({ deniedCount, missingCount, otherErrorCount }) {
    if (deniedCount > 0) {
        return 'Permission denied while loading one or more team-agent workspaces. Update workspace sharing or security rules.';
    }
    if (missingCount > 0) {
        return 'One or more team-agent workspaces could not be found and were excluded from the roster. They may have been removed or transferred; contact support if this is unexpected.';
    }
    if (otherErrorCount > 0) {
        return 'Could not load one or more team-agent workspaces. Please reload; if this persists, contact support.';
    }
    return null;
}

/**
 * @param {string|null} twId - the parent team workspace id, or a falsy
 *   value to disable resolution entirely (e.g. the viewer is not currently
 *   an owner/member of a team workspace).
 * @param {string[]|null} teamAgentWorkspaceIds - the TW doc's own
 *   teamAgentWorkspaceIds array -- TAW WORKSPACE DOCUMENT ids, NOT agent
 *   user uids (that is teamAgentIds, a different field -- see this file's
 *   header comment for why the distinction is load-bearing). Sourced from
 *   the caller's already-loaded workspace doc. No extra listener is opened
 *   here to obtain it -- reusing the boot listener's data keeps this hook's
 *   cost at N one-time reads, not N+1 listeners.
 * @returns {{taws: Array, patchTaw: Function}}
 *   taws     — resolved TAW workspace docs (id + data). Empty while loading,
 *              on error, or when disabled — never partially populated with a
 *              stale roster from a previous twId.
 *   patchTaw — (tawId, updates) => void. Patches ONE resolved TAW's fields in
 *              local state without a re-fetch or a listener. `updates` may be
 *              a plain object merged shallowly onto the matching TAW, or a
 *              function `(currentTaw) => partialUpdates` for a patch that
 *              needs to read the current value first (e.g. merging into a
 *              nested object rather than replacing it outright). No-ops if
 *              tawId isn't currently in the roster (e.g. it errored out or
 *              was removed) — never re-adds a doc this hook didn't resolve
 *              itself. Callers must only invoke this AFTER confirming the
 *              underlying write succeeded (see this file's header comment).
 */
export function useTaws(twId, teamAgentWorkspaceIds) {
    const { showToast } = useToast();
    const [taws, setTaws] = React.useState([]);
    const idsKey = Array.isArray(teamAgentWorkspaceIds) ? teamAgentWorkspaceIds.slice().sort().join(',') : '';

    React.useEffect(() => {
        if (!twId || !Array.isArray(teamAgentWorkspaceIds) || teamAgentWorkspaceIds.length === 0) {
            setTaws([]);
            return undefined;
        }

        let cancelled = false;

        (async () => {
            const results = await Promise.allSettled(
                teamAgentWorkspaceIds.map(id => getDoc(doc(db, 'workspaces', id)))
            );
            if (cancelled) return;

            results.forEach((result) => {
                if (result.status === 'rejected') {
                    console.error('Failed to load a team-agent workspace:', result.reason);
                }
            });

            const { docs, deniedCount, missingCount, otherErrorCount } = summarizeTawResults(results);

            // A failed or missing get() drops just that ONE TAW from the
            // roster — the other N-1, successfully-loaded TAWs still
            // render. What this must never do is stay silent about the
            // drop: the toast below makes the failure visible instead of
            // letting a shrunken roster read as "this team just has fewer
            // TAWs than it really does". This is the exact silent-failure
            // shape (T-41-04-SILENT) that hid TAW roster data for months --
            // useTaws previously had NO error path at all, and even after
            // 41-04 added one, a missing document (which RESOLVES rather
            // than REJECTS) still slipped through it silently until this
            // fix. `docs` is always freshly rebuilt from THIS pass's
            // results, so a failure never leaves a stale roster from a
            // prior twId either.
            setTaws(docs);

            const message = resolveTawLoadErrorMessage({ deniedCount, missingCount, otherErrorCount });
            if (message) showToast(message, 'error');
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [twId, idsKey, showToast]);

    // T-41-STALE-ROSTER: imperative, listener-free local patch -- see this
    // file's header comment. Never touches a TAW not already resolved into
    // `taws`, so a failed/missing TAW stays absent (missingCount reporting
    // above is untouched by this).
    const patchTaw = React.useCallback((tawId, updates) => {
        setTaws(prev => applyTawPatch(prev, tawId, updates));
    }, []);

    return { taws, patchTaw };
}
